import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from '@/components/ui/sheet';

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'Collection', path: '/collection' },
  { name: 'About', path: '/about' },
  { name: 'Contact', path: '/contact' },
];

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(
      'Hello, I would like to enquire about a design from Anand Fashion.'
    );
    window.open(`https://wa.me/919214456664?text=${message}`, '_blank');
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-heritage ${
        isScrolled
          ? 'bg-background/95 backdrop-blur-md shadow-soft'
          : 'bg-transparent'
      }`}
    >
      <nav className="container-heritage">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo */}
          <Link
            to="/"
            className="flex flex-col items-start group"
          >
            <span className="font-serif text-xl md:text-2xl font-medium text-foreground tracking-tight">
              Anand Fashion
            </span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mt-0.5 opacity-80">
              Est. 1994
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative text-sm tracking-wide transition-colors duration-300 link-underline ${
                  location.pathname === link.path
                    ? 'text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:block">
            <Button
              onClick={handleWhatsAppClick}
              variant="outline"
              className="border-primary/20 hover:border-primary hover:bg-primary hover:text-primary-foreground text-sm tracking-wide transition-all duration-300"
            >
              Enquire Now
            </Button>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button
                variant="ghost"
                size="icon"
                className="text-foreground"
              >
                <Menu className="h-5 w-5" />
                <span className="sr-only">Open menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-full sm:w-80 bg-background border-l border-border/50"
            >
              <div className="flex flex-col h-full pt-12">
                {/* Mobile Nav Links */}
                <nav className="flex flex-col gap-1">
                  {navLinks.map((link, index) => (
                    <SheetClose asChild key={link.path}>
                      <Link
                        to={link.path}
                        className={`py-4 px-2 text-lg font-serif tracking-wide border-b border-border/30 transition-colors duration-300 ${
                          location.pathname === link.path
                            ? 'text-foreground'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                        style={{
                          animationDelay: `${index * 0.1}s`,
                        }}
                      >
                        {link.name}
                      </Link>
                    </SheetClose>
                  ))}
                </nav>

                {/* Mobile CTA */}
                <div className="mt-auto pb-8">
                  <Button
                    onClick={() => {
                      setIsOpen(false);
                      handleWhatsAppClick();
                    }}
                    className="w-full btn-heritage-filled py-6"
                  >
                    Enquire on WhatsApp
                  </Button>
                  <p className="text-center text-xs text-muted-foreground mt-4">
                    A Legacy of Style Since 1994
                  </p>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
